import { useState, useEffect } from 'react';
import { mockInstruments, instrumentTypes } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, Plus, Search, Filter, Upload, Trash2, FileText, DollarSign } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import InstrumentTable from '../components/InstrumentTable';
import DashboardPanels from '../components/DashboardPanels';
import AddInstrumentModal from '../components/AddInstrumentModal';
import EditInstrumentModal from '../components/EditInstrumentModal';
import HistoryModal from '../components/HistoryModal';
import AssignModal from '../components/AssignModal';
import BudgetModal from '../components/BudgetModal';
import CertificatesModal from '../components/CertificatesModal';
import { useToast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Dashboard = () => {
  const [instruments, setInstruments] = useState([]);
  const [filteredInstruments, setFilteredInstruments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('Todos os tipos');
  const [selectedStatus, setSelectedStatus] = useState('Todos');
  const [selectedInstrumentStatus, setSelectedInstrumentStatus] = useState('Todos os Status');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [isCertificatesModalOpen, setIsCertificatesModalOpen] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Load instruments from localStorage or use mock data
    const savedInstruments = localStorage.getItem('instruments');
    if (savedInstruments) {
      setInstruments(JSON.parse(savedInstruments));
    } else {
      setInstruments(mockInstruments);
      localStorage.setItem('instruments', JSON.stringify(mockInstruments));
    }
  }, []);

  useEffect(() => {
    // Save to localStorage whenever instruments change
    if (instruments.length > 0) {
      localStorage.setItem('instruments', JSON.stringify(instruments));
    }
  }, [instruments]);

  useEffect(() => {
    // Filter instruments based on search, type, calibration status and instrument status
    let filtered = instruments;

    if (searchTerm) {
      filtered = filtered.filter(
        (inst) =>
          (inst && inst.nome && typeof inst.nome === 'string' && inst.nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (inst && inst.marca && typeof inst.marca === 'string' && inst.marca.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedType && selectedType !== 'Todos os tipos') {
      filtered = filtered.filter((inst) => inst && inst.tipo === selectedType);
    }

    if (selectedStatus && selectedStatus !== 'Todos') {
      filtered = filtered.filter((inst) => {
        if (!inst) return false;
        if (selectedStatus === 'Em Dia') return inst.status === 'em_estoque';
        if (selectedStatus === 'Vencido') return inst.status === 'vencido';
        return true;
      });
    }

    if (selectedInstrumentStatus && selectedInstrumentStatus !== 'Todos os Status') {
      filtered = filtered.filter((inst) => inst && inst.statusInstrumento === selectedInstrumentStatus);
    }

    setFilteredInstruments(filtered);
  }, [instruments, searchTerm, selectedType, selectedStatus, selectedInstrumentStatus]);

  const handleAddInstrument = (newInstrument) => {
    const instrumentWithId = {
      ...newInstrument,
      id: Date.now().toString(),
    };
    setInstruments([...instruments, instrumentWithId]);
    toast({
      title: 'Instrumento adicionado!',
      description: `${newInstrument.nome} foi adicionado com sucesso.`,
    });
  };

  const handleEditInstrument = (updatedInstrument) => {
    setInstruments(
      instruments.map((inst) =>
        inst.id === updatedInstrument.id ? updatedInstrument : inst
      )
    );
    toast({
      title: 'Instrumento atualizado!',
      description: `${updatedInstrument.nome} foi atualizado com sucesso.`,
    });
  };

  const handleDeleteInstrument = (id) => {
    const instrument = instruments.find((inst) => inst.id === id);
    setInstruments(instruments.filter((inst) => inst.id !== id));
    toast({
      title: 'Instrumento excluído!',
      description: `${instrument?.nome} foi removido do sistema.`,
      variant: 'destructive',
    });
  };

  const handleAssignResponsible = (instrumentId, responsible) => {
    setInstruments(
      instruments.map((inst) =>
        inst.id === instrumentId ? { ...inst, responsavel: responsible } : inst
      )
    );
    toast({
      title: 'Responsável atribuído!',
      description: `Responsável atualizado para ${responsible}.`,
    });
  };

  const handleUpdateBudget = (instrumentId, budgetData) => {
    setInstruments(
      instruments.map((inst) =>
        inst.id === instrumentId ? { ...inst, ...budgetData } : inst
      )
    );
  };

  const handleUpdateCertificates = (instrumentId, certificatesData) => {
    setInstruments(
      instruments.map((inst) =>
        inst.id === instrumentId ? { ...inst, ...certificatesData } : inst
      )
    );
  };

  const exportExpired = async () => {
    const { utils, write } = await import('xlsx');
    
    const expired = instruments.filter((inst) => inst.status === 'vencido');
    
    // Prepare data with only requested fields
    const data = expired.map((inst) => ({
      'Nome do Instrumento': inst.nome || '',
      'Tipo': inst.tipo || '',
      'Faixa de Medição': inst.faixaMedicao || '',
    }));

    // Create workbook and worksheet
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(data);

    // Get the range of the worksheet
    const range = utils.decode_range(ws['!ref']);
    
    // Style all cells - centered alignment
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;
        
        // Add cell styling
        ws[cellAddress].s = {
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true
          },
          font: {
            bold: R === 0 // Header row bold
          }
        };
      }
    }

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Nome do Instrumento
      { wch: 20 }, // Tipo
      { wch: 20 }, // Faixa de Medição
    ];

    // Add worksheet to workbook
    utils.book_append_sheet(wb, ws, 'Instrumentos Vencidos');

    // Generate Excel file
    const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Download file
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `instrumentos-vencidos-${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Exportação concluída!',
      description: `${expired.length} instrumentos vencidos exportados para Excel.`,
    });
  };

  const importInstruments = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const { read, utils } = await import('xlsx');
      
      // Read file
      const data = await file.arrayBuffer();
      const workbook = read(data);
      
      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // Convert to JSON
      const jsonData = utils.sheet_to_json(worksheet);
      
      if (jsonData.length === 0) {
        toast({
          title: 'Arquivo vazio!',
          description: 'O arquivo Excel não contém dados.',
          variant: 'destructive',
        });
        return;
      }

      // Process and add instruments
      let successCount = 0;
      const newInstruments = [];

      jsonData.forEach((row) => {
        // Helper function to parse Excel dates
        const parseExcelDate = (dateValue) => {
          if (!dateValue) return '';
          
          // Se for número (serial date do Excel)
          if (typeof dateValue === 'number') {
            // Excel date serial (days since 1900-01-01)
            const excelEpoch = new Date(1899, 11, 30);
            const date = new Date(excelEpoch.getTime() + dateValue * 86400000);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          
          // Se já for string, tentar converter
          if (typeof dateValue === 'string') {
            // Se já estiver no formato YYYY-MM-DD
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
              return dateValue;
            }
            
            // Se estiver no formato DD/MM/YYYY
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
              const [day, month, year] = dateValue.split('/');
              return `${year}-${month}-${day}`;
            }
            
            // Tentar criar data e formatar
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          }
          
          return dateValue;
        };

        // Map Excel columns to instrument fields
        const rawDate = row['Data de Vencimento'] || row['Vencimento'] || row['proximaCalibracao'] || '';
        const parsedDate = parseExcelDate(rawDate);
        
        const instrument = {
          id: Date.now().toString() + Math.random(),
          nome: row['Nome do Instrumento'] || row['Nome'] || row['nome'] || '',
          marca: row['Marca'] || row['marca'] || '',
          tipo: row['Tipo'] || row['tipo'] || '',
          faixaMedicao: row['Faixa de Medição'] || row['Faixa de Medicao'] || row['faixaMedicao'] || '',
          proximaCalibracao: parsedDate,
          responsavel: row['Responsável'] || row['Responsavel'] || row['responsavel'] || '',
          setor: row['Setor'] || row['setor'] || '',
          statusInstrumento: row['Status do Instrumento'] || row['Status'] || row['statusInstrumento'] || 'Estoque',
          orcamentoNumero: row['Orçamento Nº'] || row['Orçamento'] || row['orcamentoNumero'] || '',
          custoCalibracao: parseFloat(row['Custo Calibração'] || row['custoCalibracao'] || 0),
          custoManutencao: parseFloat(row['Custo Manutenção'] || row['custoManutencao'] || 0),
          qrCodeUrl: row['QR Code URL'] || row['qrCodeUrl'] || '',
          observacoes: row['Observações'] || row['Observacoes'] || row['observacoes'] || '',
          status: 'em_estoque', // Default status
        };

        // Calculate status based on date
        // Data retroativa (passado) = vencido
        // Data atual ou posterior = em dia
        // Independente do status do instrumento (Estoque, Emprestado, Em manutenção, Em calibração)
        if (instrument.proximaCalibracao) {
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Zerar horas para comparação apenas de data
          
          const expiryDate = new Date(instrument.proximaCalibracao);
          expiryDate.setHours(0, 0, 0, 0);
          
          // Se a data de vencimento for ANTERIOR a hoje = vencido
          // Se a data de vencimento for IGUAL ou POSTERIOR a hoje = em dia
          instrument.status = expiryDate < today ? 'vencido' : 'em_estoque';
        }

        newInstruments.push(instrument);
        successCount++;
      });

      // Add to instruments list
      setInstruments([...instruments, ...newInstruments]);

      toast({
        title: 'Importação concluída!',
        description: `${successCount} instrumentos importados com sucesso.`,
      });

      // Clear input
      event.target.value = '';
    } catch (error) {
      console.error('Error importing:', error);
      toast({
        title: 'Erro na importação!',
        description: 'Não foi possível importar o arquivo. Verifique o formato.',
        variant: 'destructive',
      });
    }
  };

  const downloadTemplate = async () => {
    const { utils, write } = await import('xlsx');
    
    // Create template data with example
    const templateData = [
      {
        'Nome do Instrumento': 'Exemplo: Paquímetro Digital',
        'Marca': 'Exemplo: Mitutoyo',
        'Tipo': 'Exemplo: Paquímetro',
        'Faixa de Medição': 'Exemplo: 0-150mm',
        'Data de Vencimento': '2025-12-31',
        'Responsável': 'Exemplo: João Silva',
        'Setor': 'Exemplo: Produção',
        'Status do Instrumento': 'Estoque',
        'Observações': 'Exemplo de observações',
      }
    ];

    // Create workbook
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Nome
      { wch: 20 }, // Marca
      { wch: 20 }, // Tipo
      { wch: 20 }, // Faixa
      { wch: 18 }, // Data
      { wch: 20 }, // Responsável
      { wch: 20 }, // Setor
      { wch: 22 }, // Status
      { wch: 30 }, // Obs
    ];

    utils.book_append_sheet(wb, ws, 'Template');

    // Generate Excel file
    const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template-importacao-instrumentos.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Template baixado!',
      description: 'Use este arquivo como modelo para importar instrumentos.',
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedInstruments([]);
      setSelectAll(false);
    } else {
      setSelectedInstruments(filteredInstruments.map(inst => inst.id));
      setSelectAll(true);
    }
  };

  const toggleSelectInstrument = (id) => {
    if (selectedInstruments.includes(id)) {
      setSelectedInstruments(selectedInstruments.filter(instId => instId !== id));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedInstruments, id];
      setSelectedInstruments(newSelected);
      if (newSelected.length === filteredInstruments.length) {
        setSelectAll(true);
      }
    }
  };

  const deleteSelectedInstruments = () => {
    if (selectedInstruments.length === 0) {
      toast({
        title: 'Nenhum instrumento selecionado!',
        description: 'Selecione instrumentos para deletar.',
        variant: 'destructive',
      });
      return;
    }

    const confirmed = window.confirm(
      `Tem certeza que deseja deletar ${selectedInstruments.length} instrumento(s) selecionado(s)? Esta ação não pode ser desfeita!`
    );

    if (confirmed) {
      const remainingInstruments = instruments.filter(inst => !selectedInstruments.includes(inst.id));
      setInstruments(remainingInstruments);
      setSelectedInstruments([]);
      setSelectAll(false);
      
      toast({
        title: 'Instrumentos deletados!',
        description: `${selectedInstruments.length} instrumento(s) removido(s) do sistema.`,
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (instrument) => {
    setSelectedInstrument(instrument);
    setIsEditModalOpen(true);
  };

  const handleHistory = (instrument) => {
    setSelectedInstrument(instrument);
    setIsHistoryModalOpen(true);
  };

  const handleAssign = (instrument) => {
    setSelectedInstrument(instrument);
    setIsAssignModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-black mb-2">
            Sistema de Gestão de Instrumentos
          </h1>
          <p className="text-lg text-slate-700">
            Rastreie e gerencie instrumentos de medição, calibração e manutenção
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <Button
            variant="outline"
            onClick={exportExpired}
            className="bg-white hover:bg-orange-50 border-orange-300 text-orange-700 shadow-sm font-medium"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar Vencidos
          </Button>
          
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="bg-white hover:bg-green-50 border-green-300 text-green-700 shadow-sm font-medium"
          >
            <FileText className="w-4 h-4 mr-2" />
            Baixar Template
          </Button>
          
          <Button
            variant="outline"
            onClick={() => document.getElementById('import-file').click()}
            className="bg-white hover:bg-blue-50 border-blue-300 text-blue-700 shadow-sm font-medium"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar Instrumentos
          </Button>
          <input
            id="import-file"
            type="file"
            accept=".xlsx,.xls"
            onChange={importInstruments}
            className="hidden"
          />

          <Button
            variant="outline"
            onClick={() => setIsBudgetModalOpen(true)}
            className="bg-white hover:bg-amber-50 border-amber-300 text-amber-700 shadow-sm font-medium"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Orçamento
          </Button>

          <Button
            variant="outline"
            onClick={() => setIsCertificatesModalOpen(true)}
            className="bg-white hover:bg-blue-50 border-blue-300 text-blue-700 shadow-sm font-medium"
          >
            <FileText className="w-4 h-4 mr-2" />
            Certificados
          </Button>
          
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-black hover:bg-slate-800 text-white shadow-lg hover:shadow-xl transition-all font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Instrumento
          </Button>

          {selectedInstruments.length > 0 && (
            <Button
              variant="outline"
              onClick={deleteSelectedInstruments}
              className="bg-white hover:bg-red-50 border-red-300 text-red-700 shadow-sm font-medium"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Deletar Selecionados ({selectedInstruments.length})
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="instruments" className="w-full">
          <TabsList className="bg-white border border-orange-200 shadow-sm mb-6">
            <TabsTrigger value="instruments" className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-900 font-medium">
              Instrumentos
            </TabsTrigger>
            <TabsTrigger value="panels" className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-900 font-medium">
              Painéis
            </TabsTrigger>
          </TabsList>

          <TabsContent value="instruments">
            {/* Filters Card */}
            <div className="bg-white rounded-xl shadow-md border border-orange-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-black">Filtros de Pesquisa</h3>
                </div>
                <div className="bg-gradient-to-r from-orange-100 to-amber-100 px-4 py-2 rounded-lg border border-orange-300">
                  <p className="text-sm font-medium text-orange-900">
                    Resultados: <span className="text-xl font-bold text-orange-700">{filteredInstruments.length}</span> / {instruments.length}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Buscar por Nome ou Marca
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Digite para pesquisar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-orange-200 focus:border-orange-500 focus:ring-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Filtrar por Tipo
                  </label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger className="border-orange-200 focus:border-orange-500 focus:ring-orange-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {instrumentTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Status da Calibração
                  </label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="border-orange-200 focus:border-orange-500 focus:ring-orange-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Todos">Todos</SelectItem>
                      <SelectItem value="Em Dia">
                        Em Dia ({instruments.filter(i => i.status === 'em_estoque').length})
                      </SelectItem>
                      <SelectItem value="Vencido">
                        Vencido ({instruments.filter(i => i.status === 'vencido').length})
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Status do Instrumento
                  </label>
                  <Select value={selectedInstrumentStatus} onValueChange={setSelectedInstrumentStatus}>
                    <SelectTrigger className="border-orange-200 focus:border-orange-500 focus:ring-orange-500">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Todos os Status">Todos os Status</SelectItem>
                      <SelectItem value="Estoque">
                        Estoque ({instruments.filter(i => i.statusInstrumento === 'Estoque').length})
                      </SelectItem>
                      <SelectItem value="Emprestado">
                        Emprestado ({instruments.filter(i => i.statusInstrumento === 'Emprestado').length})
                      </SelectItem>
                      <SelectItem value="Em manutenção">
                        Em manutenção ({instruments.filter(i => i.statusInstrumento === 'Em manutenção').length})
                      </SelectItem>
                      <SelectItem value="Em calibração">
                        Em calibração ({instruments.filter(i => i.statusInstrumento === 'Em calibração').length})
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Selection Actions */}
              {filteredInstruments.length > 0 && (
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-orange-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAll}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    {selectAll ? 'Desmarcar Todos' : 'Selecionar Todos'}
                  </Button>
                  {selectedInstruments.length > 0 && (
                    <span className="text-sm font-medium text-slate-700">
                      {selectedInstruments.length} instrumento(s) selecionado(s)
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Instruments Table */}
            <InstrumentTable
              instruments={filteredInstruments}
              onEdit={handleEdit}
              onDelete={handleDeleteInstrument}
              onHistory={handleHistory}
              onAssign={handleAssign}
              selectedInstruments={selectedInstruments}
              onSelectInstrument={toggleSelectInstrument}
              selectAll={selectAll}
              onToggleSelectAll={toggleSelectAll}
            />
          </TabsContent>

          <TabsContent value="panels">
            <DashboardPanels instruments={instruments} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <AddInstrumentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddInstrument}
      />

      <BudgetModal
        isOpen={isBudgetModalOpen}
        onClose={() => setIsBudgetModalOpen(false)}
        instruments={instruments}
        onUpdateBudget={handleUpdateBudget}
      />

      <CertificatesModal
        isOpen={isCertificatesModalOpen}
        onClose={() => setIsCertificatesModalOpen(false)}
        instruments={instruments}
        onUpdateCertificates={handleUpdateCertificates}
      />

      {selectedInstrument && (
        <>
          <EditInstrumentModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedInstrument(null);
            }}
            instrument={selectedInstrument}
            onEdit={handleEditInstrument}
          />

          <HistoryModal
            isOpen={isHistoryModalOpen}
            onClose={() => {
              setIsHistoryModalOpen(false);
              setSelectedInstrument(null);
            }}
            instrument={selectedInstrument}
          />

          <AssignModal
            isOpen={isAssignModalOpen}
            onClose={() => {
              setIsAssignModalOpen(false);
              setSelectedInstrument(null);
            }}
            instrument={selectedInstrument}
            onAssign={handleAssignResponsible}
          />
        </>
      )}
    </div>
  );
};

export default Dashboard;